import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  settings: {
    openSelectors: [
      '.cp-topnav-alarm-bell',
      '.cp-user-account-menu-item-class-lesson-alarm',
    ],
    openLabel: '수업 알람 설정',
    accessLabel: '상단 알람 종 또는 계정 메뉴',
  },
  reopenPopup: {
    openSelectors: [
      '.cp-topnav-alarm-bell.is-attention',
      '[aria-label="수업 알람 다시 열기"]',
    ],
    openLabel: '알람 다시 열기',
    accessLabel: '상단 알람 종',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'open-alarm-settings': R.settings,
  'modal-overview': R.settings,
  'toggle-master': R.settings,
  'presentation-only': R.settings,
  'channel-regular': R.settings,
  'set-offset': R.settings,
  'set-sound': R.settings,
  'schedule-tabs': R.settings,
  'test-fire': R.settings,
  'alarm-popup-close': R.settings,
  'reopen-alarm-popup': R.reopenPopup,
  'alarm-popup-stop': R.reopenPopup,
};

const classAlarmRaw: TutorialDefinition = {
  id: 'class-alarm',
  title: '알람 설정',
  summary: '수업 시작 알람을 켜고 시각·소리·미리듣기를 설정하는 방법을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '알람 설정 튜토리얼을 시작해요! 수업 시작을 알려주는 알람을 켜고 시각·소리를 정하는 방법을 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '알람 설정을 시작해요!',
      placement: 'center',
    },
    {
      id: 'open-alarm-settings',
      mascot:
        '상단의 알람 종 아이콘을 눌러 설정을 열어 주세요. (계정 메뉴의 "수업 알람 설정"으로도 열 수 있어요.)',
      targetSelector:
        '.cp-topnav-alarm-bell, .cp-user-account-menu-item-class-lesson-alarm, [data-tutorial-alarm="open-bell"]',
      placement: 'bottom',
      advanceWhenSelectorExists: '.cp-class-lesson-alarm-settings-root',
      section: '[알람] 설정 열기',
      sectionMascot: '알람 설정 창을 여는 구간이에요.',
    },
    {
      id: 'modal-overview',
      mascot:
        '수업 알람 설정에서는 일반수업, 전담수업별 알람 종류와 알람 실행 시각을 설정할 수 있어요. 알람 스케줄은 수업시간 설정과 연간시간표에 입력된 시간표에 따라 자동 생성돼요. 지금부터 하나하나 함께 살펴볼게요.',
      targetSelector: '.cp-class-lesson-alarm-settings-panel',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'toggle-master',
      mascot: '오른쪽 위 "알람" 스위치를 켜 주세요. 이미 켜져 있으면 "다음"을 눌러 주세요.',
      targetSelector:
        '[aria-label="수업 알람 켜기/끄기"], [data-tutorial-alarm="master-toggle"]',
      manualAdvanceIfExists: '[aria-label="수업 알람 켜기/끄기"].on',
      section: '[알람] 켜기·채널 설정',
      sectionMascot: '알람을 켜고 시각·소리를 정하는 구간이에요.',
    },
    {
      id: 'presentation-only',
      mascot:
        '"프레젠테이션 전용"을 켜면 메인 프레젠테이션 화면에만 알람 팝업을 띄울 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[aria-label="프레젠테이션 전용 표시 켜기/끄기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'channel-regular',
      mascot:
        '일반수업과 전담수업의 알람 종류와 알람 시작 시각을 설정할 수 있어요.',
      targetSelector: '.cp-class-lesson-alarm-channel, [data-tutorial-alarm="channel-regular"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'set-offset',
      mascot:
        '수업 시작 몇 분 전에 알려줄지 골라 주세요. 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[aria-label="일반수업 알람 알람 시각"] .cp-class-lesson-alarm-offset-btn, .cp-class-lesson-alarm-offset-btn',
      manualAdvance: true,
    },
    {
      id: 'set-sound',
      mascot:
        '알람 종류(소리)를 눌러 바꿔 볼 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector:
        '[aria-label="일반수업 알람 알람 종류"], [aria-label="일반수업 알람 알람 종류"] ~ .cp-class-lesson-alarm-kind-menu',
      spotlightUnion: true,
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'schedule-tabs',
      mascot:
        '"자동 알람 설정"과 "커스텀 알람 설정" 탭으로 오늘 미리보기·날짜별 설정을 볼 수 있어요. 탭을 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-class-lesson-alarm-schedule-tabs, .cp-class-lesson-alarm-edu-list',
      manualAdvance: true,
      section: '[알람] 스케줄·미리 듣기',
      sectionMascot: '스케줄 확인과 즉시 실행을 알아보는 구간이에요.',
    },
    {
      id: 'test-fire',
      mascot:
        '알람 스케줄과 상관없이 알람을 즉시 실행하고 싶을 때 사용해요. 버튼을 누르면 설정 창이 닫히고 팝업이 떠요. 눌러 주세요.',
      targetSelector:
        '.cp-class-lesson-alarm-test-fire, [data-tutorial-alarm="test-fire-group"]',
      advanceSelector: '.cp-class-lesson-alarm-test-fire-btn, [data-tutorial-alarm="test-fire"]',
      advanceWhenSelectorExists: '.cp-class-lesson-alarm-popup-overlay',
      manualAdvanceIfExists: '.cp-class-lesson-alarm-popup-overlay',
    },
    {
      id: 'alarm-popup-close',
      mascot:
        '실제 알람이 울릴 때와 같은 팝업이에요. 먼저 "팝업닫기"를 눌러 주세요. (알람 소리는 계속 나요.)',
      targetSelector: '.cp-class-lesson-alarm-popup-content',
      placement: 'top',
      advanceSelector: '[data-tutorial-alarm="popup-close"]',
      advanceImmediately: true,
      blockClickSelectors: '[data-tutorial-alarm="popup-stop"]',
      advanceWhenSelectorGone: '.cp-class-lesson-alarm-popup-overlay',
    },
    {
      id: 'reopen-alarm-popup',
      mascot: '네비바의 알람 종(두근두근)을 눌러 팝업을 다시 열어 주세요.',
      targetSelector:
        '.cp-topnav-alarm-bell.is-attention, [aria-label="수업 알람 다시 열기"]',
      placement: 'bottom',
      advanceWhenSelectorExists: '.cp-class-lesson-alarm-popup-overlay',
      targetMissingHint:
        '알람 종이 두근거리지 않으면, 앞에서 "팝업닫기"로 닫은 뒤 다시 진행해 주세요.',
    },
    {
      id: 'alarm-popup-stop',
      mascot:
        '"알람끄기"를 눌러 알람을 완전히 꺼 주세요. 이미 껐다면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-class-lesson-alarm-popup-content',
      placement: 'top',
      advanceSelector: '[data-tutorial-alarm="popup-stop"]',
      advanceImmediately: true,
      blockClickSelectors: '[data-tutorial-alarm="popup-close"]',
      advanceWhenSelectorGone: '.cp-class-lesson-alarm-popup-overlay',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      targetMissingHint:
        '알람 팝업이 없으면 이미 꺼진 상태일 수 있어요. "다음"을 눌러 완료하거나, 두근거리는 종으로 다시 연 뒤 "알람끄기"를 눌러 주세요.',
    },
    {
      id: 'outro',
      mascot:
        '알람 설정 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const classAlarm: TutorialDefinition = {
  ...classAlarmRaw,
  steps: classAlarmRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default classAlarm;
